import { useEffect, useCallback, useRef } from 'react';

/**
 * DRM protection configuration
 */
export interface DrmProtectionConfig {
  disableRightClick?: boolean;
  disableTextSelection?: boolean;
  disableKeyboardShortcuts?: boolean;
  detectDevTools?: boolean;
  disablePrint?: boolean;
  onDevToolsDetected?: () => void;
  onViolationAttempt?: (type: string) => void;
}

/**
 * Hook to apply DRM security protections to prevent content theft.
 * Implements multiple layers of protection for DRM-protected content.
 */
export function useDrmProtection(config: DrmProtectionConfig = {}) {
  const {
    disableRightClick = true,
    disableTextSelection = true,
    disableKeyboardShortcuts = true,
    detectDevTools = true,
    disablePrint = true,
    onDevToolsDetected,
    onViolationAttempt,
  } = config;

  const devToolsDetectedRef = useRef(false);
  const devToolsCheckInterval = useRef<NodeJS.Timeout | null>(null);

  /**
   * Disable right-click context menu
   */
  const handleContextMenu = useCallback((e: MouseEvent) => {
    if (disableRightClick) {
      e.preventDefault();
      onViolationAttempt?.('right-click');
      console.warn('DRM Protection: Right-click disabled');
    }
  }, [disableRightClick, onViolationAttempt]);

  /**
   * Disable keyboard shortcuts (Ctrl+C, Ctrl+S, Ctrl+P, Ctrl+U)
   */
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (disableKeyboardShortcuts) {
      // Ctrl+C (Copy), Cmd+C (Mac)
      if ((e.ctrlKey || e.metaKey) && e.key === 'c') {
        e.preventDefault();
        onViolationAttempt?.('copy-shortcut');
        console.warn('DRM Protection: Copy shortcut disabled');
      }
      
      // Ctrl+S (Save), Cmd+S (Mac)
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        onViolationAttempt?.('save-shortcut');
        console.warn('DRM Protection: Save shortcut disabled');
      }
      
      // Ctrl+P (Print), Cmd+P (Mac)
      if ((e.ctrlKey || e.metaKey) && e.key === 'p') {
        e.preventDefault();
        onViolationAttempt?.('print-shortcut');
        console.warn('DRM Protection: Print shortcut disabled');
      }
      
      // Ctrl+U (View Source), Cmd+Option+U (Mac)
      if ((e.ctrlKey || e.metaKey) && e.key === 'u') {
        e.preventDefault();
        onViolationAttempt?.('view-source');
        console.warn('DRM Protection: View source disabled');
      }

      // F12 (DevTools)
      if (e.key === 'F12') {
        e.preventDefault();
        onViolationAttempt?.('devtools-f12');
        console.warn('DRM Protection: F12 DevTools disabled');
      }

      // Ctrl+Shift+I (DevTools), Cmd+Option+I (Mac)
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'I') {
        e.preventDefault();
        onViolationAttempt?.('devtools-shortcut');
        console.warn('DRM Protection: DevTools shortcut disabled');
      }

      // Ctrl+Shift+J (Console), Cmd+Option+J (Mac)
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'J') {
        e.preventDefault();
        onViolationAttempt?.('console-shortcut');
        console.warn('DRM Protection: Console shortcut disabled');
      }

      // Ctrl+Shift+C (Inspect Element), Cmd+Option+C (Mac)
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'C') {
        e.preventDefault();
        onViolationAttempt?.('inspect-shortcut');
        console.warn('DRM Protection: Inspect shortcut disabled');
      }
    }
  }, [disableKeyboardShortcuts, onViolationAttempt]);

  /**
   * Disable print dialog
   */
  const handleBeforePrint = useCallback((e: Event) => {
    if (disablePrint) {
      e.preventDefault();
      onViolationAttempt?.('print-dialog');
      alert('Printing is disabled for DRM-protected content.');
      console.warn('DRM Protection: Print dialog disabled');
    }
  }, [disablePrint, onViolationAttempt]);

  /**
   * Detect DevTools opening
   * Uses window size differential to detect when DevTools docks
   * Disabled in development mode for better DX
   */
  const checkDevTools = useCallback(() => {
    // Skip DevTools detection in development mode
    if (import.meta.env.DEV) {
      return;
    }

    const threshold = 160; // Width/height threshold for DevTools detection
    const widthDiff = window.outerWidth - window.innerWidth;
    const heightDiff = window.outerHeight - window.innerHeight;

    if (widthDiff > threshold || heightDiff > threshold) {
      // if (!devToolsDetectedRef.current) {
      //   devToolsDetectedRef.current = true;
      //   console.error('DRM Protection: Developer tools detected!');
      //   onDevToolsDetected?.();
      //   onViolationAttempt?.('devtools-detected');

      //   // Optional: Blur content or terminate session
      //   alert('Developer tools detected. This session will be terminated for security reasons.');

      //   // You can terminate the session here if needed
      //   // window.location.reload();
      // }
      devToolsDetectedRef.current = false;
    } else {
      devToolsDetectedRef.current = false;
    }
  }, [onDevToolsDetected, onViolationAttempt]);

  /**
   * Apply text selection protection via CSS
   */
  useEffect(() => {
    if (disableTextSelection) {
      // Add global styles to disable text selection
      const style = document.createElement('style');
      style.id = 'drm-protection-styles';
      style.textContent = `
        * {
          user-select: none !important;
          -webkit-user-select: none !important;
          -moz-user-select: none !important;
          -ms-user-select: none !important;
        }
        
        /* Re-enable selection for input fields */
        input, textarea {
          user-select: text !important;
          -webkit-user-select: text !important;
        }
      `;
      document.head.appendChild(style);

      return () => {
        const existingStyle = document.getElementById('drm-protection-styles');
        if (existingStyle) {
          existingStyle.remove();
        }
      };
    }
  }, [disableTextSelection]);

  /**
   * Set up event listeners
   */
  useEffect(() => {
    // Context menu (right-click)
    if (disableRightClick) {
      document.addEventListener('contextmenu', handleContextMenu);
    }

    // Keyboard shortcuts
    if (disableKeyboardShortcuts) {
      document.addEventListener('keydown', handleKeyDown);
    }

    // Print dialog
    if (disablePrint) {
      window.addEventListener('beforeprint', handleBeforePrint);
    }

    // DevTools detection
    if (detectDevTools) {
      devToolsCheckInterval.current = setInterval(checkDevTools, 1000);
    }

    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('beforeprint', handleBeforePrint);
      
      if (devToolsCheckInterval.current) {
        clearInterval(devToolsCheckInterval.current);
      }
    };
  }, [
    disableRightClick,
    disableKeyboardShortcuts,
    disablePrint,
    detectDevTools,
    handleContextMenu,
    handleKeyDown,
    handleBeforePrint,
    checkDevTools,
  ]);

  return {
    isProtected: true,
    protections: {
      rightClick: disableRightClick,
      textSelection: disableTextSelection,
      keyboardShortcuts: disableKeyboardShortcuts,
      devTools: detectDevTools,
      print: disablePrint,
    },
  };
}
